import React, { useState, useEffect } from 'react'
import { Plus, Menu, Shield, CheckCircle, Settings, Filter, RotateCcw } from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import Breadcrumb from '../../components/Common/Breadcrumb'
import HelpModal from '../../components/HelpModal/HelpModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import submoduleService from '../../services/submoduleService'
import menuItemService from '../../services/menuItemService'

const MODULE_TYPES = [
  { value: '', label: 'Select Type' },
  { value: 'Master', label: 'Master' },
  { value: 'Transaction', label: 'Transaction' },
  { value: 'Reports', label: 'Reports' }
]

const SubmoduleMaster = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const [showHelp, setShowHelp] = useState(false)
  const [businessFunctions, setBusinessFunctions] = useState([])
  const [filter, setFilter] = useState({ moduleName: '', type: '' })
  const [appliedFilter, setAppliedFilter] = useState({ moduleName: '', type: '' })

  const loadData = async () => {
    try {
      setLoading(true)
      const params = { includeInactive: true }
      if (appliedFilter.moduleName) params.moduleName = appliedFilter.moduleName
      if (appliedFilter.type) params.type = appliedFilter.type
      const [itemsRes, statsRes] = await Promise.all([
        submoduleService.getAllSubmodules(params),
        submoduleService.getSubmoduleStats().catch(() => ({ success: false }))
      ])
      if (itemsRes?.success && Array.isArray(itemsRes.data)) {
        setData(itemsRes.data.map(item => ({ ...item, id: item._id })))
      }
      if (statsRes?.success && statsRes.data) {
        setStats({
          total: statsRes.data.total ?? 0,
          active: statsRes.data.active ?? 0,
          inactive: statsRes.data.inactive ?? 0
        })
      }
    } catch (err) {
      console.error(err)
      toast.error('Failed to load modules')
      setData([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const loadMenuItems = async () => {
      try {
        const res = await menuItemService.getAllMenuItems({ includeInactive: true })
        if (res?.success && Array.isArray(res.data)) {
          setBusinessFunctions(res.data.map(item => ({ value: item._id, label: item.label || item.menuCode })))
        }
      } catch (err) {
        console.error(err)
      }
    }
    loadMenuItems()
  }, [])

  useEffect(() => {
    loadData()
  }, [appliedFilter])

  const handleApplyFilter = () => setAppliedFilter({ ...filter })
  const handleResetFilter = () => {
    setFilter({ moduleName: '', type: '' })
    setAppliedFilter({ moduleName: '', type: '' })
  }

  const columns = [
    { accessorKey: 'sortOrder', header: 'Order', cell: ({ getValue }) => <span className="code-text">{getValue() ?? 0}</span> },
    { accessorKey: 'name', header: 'Title', cell: ({ getValue }) => <span className="name-text">{getValue()}</span> },
    { accessorKey: 'displayName', header: 'Display Name', cell: ({ row }) => <span className="description-text">{row.original.displayName || row.original.name || '-'}</span> },
    { accessorKey: 'isActive', header: 'Disabled', cell: ({ getValue }) => <span className={`status-badge ${getValue() ? 'inactive' : 'active'}`}>{getValue() ? 'No' : 'Yes'}</span> },
    { accessorKey: 'isDisplay', header: 'Is Display', cell: ({ getValue }) => <span className={`status-badge ${getValue() ? 'active' : 'inactive'}`}>{getValue() ? 'Yes' : 'No'}</span> },
    { accessorKey: 'menuItem', header: 'Business Function', cell: ({ row }) => (row.original.menuItem?.label || '-') },
    { accessorKey: 'type', header: 'Type', cell: ({ getValue }) => getValue() || 'Master' }
  ]

  const handleAddNew = () => navigate('/settings/submodule-master/form')
  const handleView = (row) => navigate('/settings/submodule-master/view', { state: { submoduleData: row } })
  const handleEdit = (row) => navigate('/settings/submodule-master/edit', { state: { submoduleData: row } })
  const handleDelete = (row) => {
    setDeleteItem(row)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem?.id) return
    try {
      const res = await submoduleService.deleteSubmodule(deleteItem.id)
      if (res?.success) {
        toast.success('Module deleted')
        setShowDeleteModal(false)
        setDeleteItem(null)
        loadData()
      } else {
        toast.error(res?.message || 'Failed to delete')
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to delete')
    }
  }

  const helpContent = (
    <>
      <h3>What is Module Management?</h3>
      <p>Sidebar items are <strong>Business Functions</strong> (e.g. Sales, Settings). <strong>Modules</strong> are the cards/screens under <strong>Masters</strong>, <strong>Transactions</strong>, and <strong>Reports</strong> within each Business Function. Here you manage those modules: title, display name, type, and whether they are disabled or visible.</p>
      <h3>Filter</h3>
      <p><strong>Module Name</strong> – Business Function (sidebar menu). <strong>Types</strong> – Master, Transaction, or Reports. Use Apply Filter to refresh the list.</p>
      <h3>Columns</h3>
      <p><strong>Order</strong> – Sort order. <strong>Title</strong> – Internal name. <strong>Display Name</strong> – Label shown in UI. <strong>Disabled</strong> – Yes if inactive. <strong>Is Display</strong> – Shown in UI. <strong>Business Function</strong> – Linked sidebar menu.</p>
    </>
  )

  const breadcrumbItems = [
    { label: 'Settings', path: '/settings', onClick: () => navigate('/settings') },
    { label: 'Masters', path: '/settings', onClick: () => navigate('/settings') },
    { label: 'Module Management', path: '/settings/submodule-master', isCurrent: true }
  ]

  return (
    <div className="sales-dashboard">
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          rightSlot={
            <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
              <Menu size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>

      <div className="page-header">
        <div className="page-title">
          <h1>Module Management</h1>
          <p>Manage modules under Masters, Transactions and Reports</p>
        </div>
        <div className="page-actions">
          <button className="btn-primary" onClick={handleAddNew}>
            <Plus size={18} />
            Add New
          </button>
        </div>
      </div>

      {/* Filter section - reference: Module Name (Business Function) + Types */}
      <div className="form-container" style={{ marginBottom: '1rem' }}>
        <div className="sac-form-header">
          <h2>Module</h2>
        </div>
        <div className="form-content" style={{ padding: '16px 20px' }}>
          <div className="grid-row-4" style={{ marginBottom: 0, alignItems: 'end' }}>
            <div className="form-group">
              <label htmlFor="filter-moduleName" className="form-label required">Module Name (Business Function)</label>
              <select
                id="filter-moduleName"
                value={filter.moduleName}
                onChange={(e) => setFilter(f => ({ ...f, moduleName: e.target.value }))}
                className="form-input"
              >
                <option value="">Select Module Name</option>
                {businessFunctions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="filter-type" className="form-label required">Types</label>
              <select
                id="filter-type"
                value={filter.type}
                onChange={(e) => setFilter(f => ({ ...f, type: e.target.value }))}
                className="form-input"
              >
                {MODULE_TYPES.map(opt => (
                  <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group d-flex gap-2" style={{ flexDirection: 'row', gap: '8px' }}>
              <button type="button" className="btn-primary" onClick={handleApplyFilter}>
                <Filter size={16} />
                Apply Filter
              </button>
              <button type="button" className="btn-secondary" onClick={handleResetFilter}>
                <RotateCcw size={16} />
                Reset Filter
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon"><Shield size={16} /></div>
            <div className="metric-trend positive"><span>{stats.total}</span></div>
          </div>
          <div className="metric-value">{stats.total}</div>
          <div className="metric-title">Total</div>
          <div className="metric-subtitle">All modules</div>
        </div>
        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon"><CheckCircle size={16} /></div>
            <div className="metric-trend positive"><span>{stats.active}</span></div>
          </div>
          <div className="metric-value">{stats.active}</div>
          <div className="metric-title">Active</div>
          <div className="metric-subtitle">In use</div>
        </div>
        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon"><Settings size={16} /></div>
            <div className="metric-trend neutral"><span>{stats.inactive}</span></div>
          </div>
          <div className="metric-value">{stats.inactive}</div>
          <div className="metric-title">Inactive</div>
          <div className="metric-subtitle">Disabled</div>
        </div>
      </div>

      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          noCard
          pageSize={10}
          className="sac-master-table"
        />
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setDeleteItem(null) }}
        onConfirm={confirmDelete}
        title="Delete Module"
        message={deleteItem ? `Delete "${deleteItem.name}"? This cannot be undone.` : ''}
        confirmText="Delete"
        type="danger"
      />

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Module Management – Help">
        {helpContent}
      </HelpModal>
    </div>
  )
}

export default SubmoduleMaster
